"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Configuration = void 0;
/**
 * Represents the SDK configuration.
 *
 * This configuration sets the default remote API url of the core-features API
 * as well as defines an environment under which the SDK will run.
 *
 * By default, clients can just do: `new Configuration()` and get a dev env client.
 */
class Configuration {
    /**
     * The base URL of the remote core-features API. If not provided, it falls
     * back to the `FEATURES_BASE_URL` env var, and defaults to the DEV env as
     * a last resort.
     */
    baseUrl;
    /**
     * The max number of entries in the cache. If not provided, it falls back
     * to the `FEATURES_MAX_CACHE_SIZE` env var, and defaults to 100 if not provided.
     */
    cacheMaxSize;
    /**
     * The max time an entry stays in the cache in seconds. If not provided, it falls back
     * to the `FEATURES_CACHE_TTL` env var, and defaults to 3600s if not provided.
     */
    cacheTTLInSeconds;
    /**
     * An optional function that can perform an action when it
     * receives a FeatureAssignment.
     *
     * This must be defined in order to use CoreFeatures Experiments,
     * in order to log the assignments key being evaluated.
     */
    assignmentListener;
    /**
     * The optional TargetedRuntime properties that should be applied to each
     * evaluation by default.
     */
    defaultTargetedRuntime;
    /**
     * The optional flag to hydrate the targeting properties on the edge-core-api level.
     * This is useful when the client doesn't inject all the user/business-specific targeting properties.
     *
     * **NOTE:** It would affect all the evaluations since it's the top level configuration.
     *
     * Disabled by default to not hydrate the targeting properties until explicitly enabled.
     * @default false
     */
    hydrateTargetingProperties;
    /**
     * Builds a new Configuration object from an optional configuration settings object.
     *
     * @param configuration the optional Configuration object to build from.
     */
    constructor(configuration) {
        const _baseUrl = configuration?.baseUrl ??
            (process.env.FEATURES_BASE_URL || 'https://api.gg-dev.link');
        const _cacheMaxSize = configuration?.cacheMaxSize ??
            parseInt(process.env.FEATURES_MAX_CACHE_SIZE || '100');
        const _cacheTTLInSeconds = configuration?.cacheTTLInSeconds ??
            parseInt(process.env.FEATURES_CACHE_TTL || '3600');
        if (!_baseUrl?.trim()) {
            throw Error(`Unable to configure the SDK for the baseUrl: ${_baseUrl}.`);
        }
        this.baseUrl = _baseUrl;
        this.cacheMaxSize = _cacheMaxSize;
        this.cacheTTLInSeconds = _cacheTTLInSeconds;
        this.assignmentListener = configuration?.assignmentListener;
        this.defaultTargetedRuntime = configuration?.defaultTargetedRuntime;
        this.hydrateTargetingProperties = configuration?.hydrateTargetingProperties;
    }
}
exports.Configuration = Configuration;
